import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';
import { DatePicker } from 'ui-components/components/date-picker';
import { DropdownMenuPosition } from 'ui-components/components/INTERNAL/dropdown/dropdown-menu-position';

import Global from '../../../store/global-selectors';
import { convertDateToMomentDateObjAtMidnightInSellerTimeZone } from '../../../services/datetime-service';

import style from './style.module.less';

const messages = defineMessages({
  from: {
    description: 'dateRangePicker.from',
    defaultMessage: 'From',
    id: 'gy5/dh',
  },
  to: {
    description: 'dateRangePicker.to',
    defaultMessage: 'To',
    id: 'kBLVR/',
  },
});

/**
 * @typedef {string | object | null} AdvancedSearchDate
 */

/**
 * @param {object} props
 * @param {AdvancedSearchDate} [props.to]
 * @param {AdvancedSearchDate} [props.from]
 * @param {string} props.dateFormat
 * @param {(dates: { to: AdvancedSearchDate, from: AdvancedSearchDate }) => void} props.setDates
 * @param {import('ui-components/components/dropdown/dropdown-types').DropdownMenuPositions} [props.calendarPosition]
 * @returns {React.JSX.Element}
 */
const DateRangePicker = ({ to = null, from = null, dateFormat, setDates, calendarPosition = DropdownMenuPosition.TopLeft }) => {
  const intl = useIntl();
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  const updateDateFilter = useCallback(
    (name, value) => {
      const datePickerTimestamp = moment(value, dateFormat).toISOString();
      const fromDate = datePickerTimestamp
        ? convertDateToMomentDateObjAtMidnightInSellerTimeZone(datePickerTimestamp, sellerTimeZone)
        : null;
      const toDate = datePickerTimestamp
        ? convertDateToMomentDateObjAtMidnightInSellerTimeZone(datePickerTimestamp, sellerTimeZone).endOf('day')
        : null;
      const utcDate = name === 'from' ? fromDate : toDate;

      setDates({ to, from, [name]: utcDate });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [setDates, dateFormat, to, from],
  );

  const onFromClickCallback = useCallback(
    (value) => {
      updateDateFilter('from', value);
    },
    [updateDateFilter],
  );

  const onToClickCallback = useCallback(
    (value) => {
      updateDateFilter('to', value);
    },
    [updateDateFilter],
  );

  const fromMoment = from && moment(from);
  const toMoment = to && moment(to);

  return (
    <div className={style['date-pickers-container']}>
      <DatePicker
        allowPastDates
        calendarPosition={calendarPosition}
        className={classNames(style['from-date-picker'], style['date-picker'])}
        dateFormat={dateFormat}
        onChange={onFromClickCallback}
        placeholder={intl.formatMessage(messages.from)}
        shouldInputHaveFullWidth
        value={fromMoment || ''}
      />
      <DatePicker
        allowPastDates
        calendarPosition={calendarPosition}
        className={style['date-picker']}
        dateFormat={dateFormat}
        onChange={onToClickCallback}
        placeholder={intl.formatMessage(messages.to)}
        shouldInputHaveFullWidth
        value={toMoment || ''}
      />
    </div>
  );
};

export default DateRangePicker;
